/**
 * `useRegisterMetrics` — role-scoped per-register aggregates for the dashboard
 * tiles (04-05).
 *
 * For the signed-in role this hook:
 *   1. fetches each of the five registers via `useRegister` (04-02), and
 *   2. SCOPES each register's records through `applyScope` (04-03) using the
 *      role's default token (`defaultScopeFor`) BEFORE aggregating, then
 *   3. computes per-register metrics: total, open, overdue, plus the
 *      register-specific cuts the tiles surface — Incidents: reportable and
 *      awaiting-a-decision counts; Risk: by-rating buckets (heatmap data).
 *
 * Every cut reads through `lib/registerReaders.ts` (13-16), the same readers the register
 * table and views use, so the tile and the list can never disagree for the same rows.
 *
 * Scope is applied BEFORE aggregation so every count reflects exactly what the
 * role may see (e.g. a Finance login's "open" is open-among-finance-relevant,
 * not the global open). This is the whole point of role-aware tiles.
 *
 * EMPTY-STATE HONESTY (04-CONTEXT.md, plan acceptance): the registers are empty
 * today (lists may be unprovisioned → `useRegister` returns `[]`). This hook
 * yields ZEROED metrics for every register, never throws. `isLoading` is true
 * while ANY register is still loading; `error` is the first register error.
 *
 * REACT HOOKS RULE: `useRegister` is called for all five keys UNCONDITIONALLY in
 * a fixed order (no loops / conditionals around the hook calls) so hook order is
 * stable across renders. The aggregation is `useMemo`'d on the fetched data.
 */

import { useMemo, useState } from 'react';
import { useRegister } from './useRegister';
import { useAuth } from '../auth/useAuth';
import { applyScope } from '../lib/scope';
import type { Identity } from '../lib/scope';
import { defaultScopeFor } from '../config/roles';
import { SCOPE_CONFIG } from '../config/scopeConfig';
import {
  isAwaitingDecision,
  isOpen,
  isOverdue,
  isReportable,
  riskRatingOf,
} from '../lib/registerReaders';
import type {
  RegisterKey,
  AnyRegisterRecord,
  RegisterRecord,
} from '../types/qms';

// ---------------------------------------------------------------------------
// Metric shapes
// ---------------------------------------------------------------------------

/** Per-register metrics the tiles render. */
export interface RegisterMetrics {
  /** Total records the role can see (after scope). */
  total: number;
  /** Open = status NOT in {Completed, Cancelled}. */
  open: number;
  /**
   * Overdue = open AND the register's own due date is before today (Melbourne days). Always 0
   * for Feedback, which has no due-date column.
   */
  overdue: number;
  /**
   * Incidents only: `ReportabilityDecision` ∈ {SIRS-P1, SIRS-P2, NDIS-Reportable, WorkSafe}
   * (the live `Reportable incidents` view). `undefined` for other registers.
   */
  reportable?: number;
  /**
   * Incidents only: open incidents whose decision is still `Pending` / blank / `Other` (the
   * number C3's Limb B escalates on). `undefined` for other registers.
   */
  awaitingDecision?: number;
  /**
   * Incidents only (13-29, the briefing's patch line): open AND reportable on `ReportabilityDecision`.
   * `undefined` for other registers.
   */
  openReportable?: number;
  /**
   * Risk only: count of items per rating bucket (the heatmap data): the residual rating, else
   * the inherent one (`riskRatingOf`), plus an 'Unrated' bucket when neither is set.
   * `undefined` for other registers.
   */
  ratingBuckets?: Record<string, number>;
}

/** The full result: a metrics map keyed by register + load/error state. */
export interface UseRegisterMetricsResult {
  metrics: Record<RegisterKey, RegisterMetrics>;
  isLoading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Aggregation (pure — exported for useRegisterMetrics.test.ts)
// ---------------------------------------------------------------------------

/** Compute the metrics for one register's already-scoped records. */
export function aggregateRegister(
  registerKey: RegisterKey,
  scoped: RegisterRecord[],
  now: number,
): RegisterMetrics {
  const metrics: RegisterMetrics = {
    total: scoped.length,
    open: scoped.filter(isOpen).length,
    overdue: scoped.filter((r) => isOverdue(r, now)).length,
  };

  // Incidents — reportable, and open with no reportability decision yet.
  if (registerKey === 'incidents') {
    metrics.reportable = scoped.filter(isReportable).length;
    metrics.awaitingDecision = scoped.filter(isAwaitingDecision).length;
    metrics.openReportable = scoped.filter((r) => isOpen(r) && isReportable(r)).length;
  }

  // Risk — by-rating buckets (heatmap data): residual, else inherent, else Unrated.
  if (registerKey === 'risk') {
    const buckets: Record<string, number> = {};
    for (const r of scoped) {
      const bucket = riskRatingOf(r)?.rating ?? 'Unrated';
      buckets[bucket] = (buckets[bucket] ?? 0) + 1;
    }
    metrics.ratingBuckets = buckets;
  }

  return metrics;
}

/** Zeroed metrics for a register (the empty-state default — never a throw). */
export function emptyMetrics(registerKey: RegisterKey): RegisterMetrics {
  const base: RegisterMetrics = { total: 0, open: 0, overdue: 0 };
  if (registerKey === 'incidents') {
    base.reportable = 0;
    base.awaitingDecision = 0;
    base.openReportable = 0;
  }
  if (registerKey === 'risk') base.ratingBuckets = {};
  return base;
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

/**
 * Aggregate role-scoped metrics across all five registers for the signed-in
 * user. Tiles (04-05) consume this.
 */
export function useRegisterMetrics(): UseRegisterMetricsResult {
  const { user, roles } = useAuth();

  // React hooks rule: call useRegister for all five keys UNCONDITIONALLY in a
  // fixed order — no loops / conditionals around the hook calls.
  const incidents = useRegister('incidents');
  const complaints = useRegister('complaints');
  const ci = useRegister('ci');
  const feedback = useRegister('feedback');
  const risk = useRegister('risk');

  const isLoading =
    incidents.isLoading ||
    complaints.isLoading ||
    ci.isLoading ||
    feedback.isLoading ||
    risk.isLoading;

  // First non-null register error (read failures surface honestly, but an empty
  // / unprovisioned register is NOT an error — useRegister returns [] for that).
  const error =
    incidents.error ??
    complaints.error ??
    ci.error ??
    feedback.error ??
    risk.error ??
    null;

  // Build the minimal scope identity (04-03). businessUnits is EMPTY → Portfolio
  // stays in its grouped fallback (widens nothing; no per-CM BU map yet, §8).
  const identity: Identity = useMemo(
    () => ({ upn: user?.email ?? '', businessUnits: [] }),
    [user?.email],
  );

  // A stable per-mount "now" for the overdue cut. Seeded once via useState's lazy
  // initializer (React-pure — NOT called during render, so the purity rule is
  // satisfied) rather than `Date.now()` inside the memo. Overdue is a
  // time-relative cut; a per-mount reference time is correct and deterministic.
  const [now] = useState(() => Date.now());

  const metrics = useMemo<Record<RegisterKey, RegisterMetrics>>(() => {
    const sources: Record<RegisterKey, AnyRegisterRecord[]> = {
      incidents: incidents.data,
      complaints: complaints.data,
      ci: ci.data,
      feedback: feedback.data,
      risk: risk.data,
    };

    const keys: RegisterKey[] = [
      'incidents',
      'complaints',
      'ci',
      'feedback',
      'risk',
    ];

    const out = {} as Record<RegisterKey, RegisterMetrics>;
    for (const key of keys) {
      const token = defaultScopeFor(roles, key);
      // Role can't view this register → render nothing for it (zeroed tile).
      if (token === null) {
        out[key] = emptyMetrics(key);
        continue;
      }
      // SCOPE FIRST (04-03), then aggregate — every count reflects the role's
      // visible slice. Empty registers → zeroed metrics (never a throw).
      const scoped = applyScope(
        token,
        identity,
        key,
        sources[key],
        SCOPE_CONFIG,
      );
      out[key] = aggregateRegister(key, scoped, now);
    }
    return out;
  }, [
    roles,
    identity,
    now,
    incidents.data,
    complaints.data,
    ci.data,
    feedback.data,
    risk.data,
  ]);

  return { metrics, isLoading, error };
}
